/**
 * Post Service for Loopino Frontend
 * Handles all post-related API operations
 * 
 * Features:
 * - List posts with filtering and pagination
 * - Get post details
 * - Update post content
 * - Approve posts
 * - Schedule posts  
 * - Delete posts
 * - Bulk operations
 * 
 * Author: Loopino Integration Team
 * Version: 1.0.0
 */

import { useApi } from '../hooks/useApi';
import { API_CONFIG } from '../config/api';
import { trackEvent } from '../utils/analytics';

class PostService {
  constructor() {
    this.apiHook = null;
    this.initializationPromise = null;
    this.resolveInitialization = null;
  }

  // Initialize with API hook (needs to be called from React component)
  initialize(apiHook) {
    // If already initialized with same hook, skip
    if (this.apiHook === apiHook) {
      return;
    }

    this.apiHook = apiHook;
    this.logInfo('PostService initialized');

    // Resolve any pending initialization promise
    if (this.resolveInitialization) {
      this.resolveInitialization();
      this.resolveInitialization = null;
      this.initializationPromise = null;
    }
  }

  // Logging utility
  logInfo(message, data = null) {
    console.log(`[PostService] ${message}`, data || '');
  }

  logError(message, error = null) {
    console.error(`[PostService] ERROR: ${message}`, error || '');
  }

  // Ensure API hook is initialized - with auto-wait
  async ensureInitialized() {
    if (this.apiHook) {
      return; // Already initialized
    }

    // Create a promise that resolves when initialize() is called
    if (!this.initializationPromise) {
      this.initializationPromise = new Promise((resolve) => {
        this.resolveInitialization = resolve;
      });
    }

    // Wait for initialization with timeout
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('PostService initialization timeout')), 5000);
    });

    await Promise.race([this.initializationPromise, timeoutPromise]);
  }

  /**
   * List posts with filters and pagination
   * @param {Object} filters - Filter parameters
   * @returns {Promise<Object>} Posts list with pagination
   */
  async listPosts(filters = {}) {
    await this.ensureInitialized();
    this.logInfo('Fetching posts list', filters);

    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (filters.campaign_id) queryParams.append('campaign_id', filters.campaign_id);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.platform) queryParams.append('platform', filters.platform);
      if (filters.week_index !== undefined) queryParams.append('week_index', filters.week_index.toString());
      if (filters.content_type) queryParams.append('content_type', filters.content_type);
      if (filters.start_date) queryParams.append('start_date', filters.start_date);
      if (filters.end_date) queryParams.append('end_date', filters.end_date);
      if (filters.limit !== undefined) queryParams.append('limit', filters.limit.toString());
      if (filters.offset !== undefined) queryParams.append('offset', filters.offset.toString());

      const endpoint = `${API_CONFIG.ENDPOINTS.POSTS.LIST}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await this.apiHook.get(endpoint);
      
      this.logInfo('Posts list fetched successfully', {
        postsCount: response.posts?.length || 0,
        pagination: response.pagination || {}
      });

      // Normalize campaign name field from backend (campaign_name -> campaignName)
      const normalizedPosts = Array.isArray(response.posts)
        ? response.posts.map(p => {
            if (!p?.campaignName && p?.campaign_name) {
              return { ...p, campaignName: p.campaign_name };
            }
            return p;
          })
        : response.posts;

      return { ...response, posts: normalizedPosts };
    } catch (error) {
      this.logError('Failed to fetch posts list', error);
      throw error;
    }
  }

  /**
   * Get post details by ID
   * @param {string} postId - Post ID
   * @returns {Promise<Object>} Post details
   */
  async getPost(postId) {
    await this.ensureInitialized();
    this.logInfo('Fetching post details', { postId });

    try {
      const response = await this.apiHook.get(API_CONFIG.ENDPOINTS.POSTS.GET(postId));
      this.logInfo('Post details fetched successfully', { postId, status: response.status });
      return response;
    } catch (error) {
      this.logError('Failed to fetch post details', error);
      throw error;
    }
  }

  /**
   * Update post content and metadata with efficient media handling
   * @param {string} postId - Post ID
   * @param {Object} postData - Updated post data
   * @param {Array<Object>} mediaUpdates - Optional media updates array with {type: 'file'|'url', file: File, url: string}
   * @returns {Promise<Object>} Updated post
   */
  async updatePost(postId, postData, mediaUpdates = null) {
    await this.ensureInitialized();
    this.logInfo('Updating post', { postId, postData, hasMediaUpdates: !!mediaUpdates });

    let mediaCounts = null;

    try {
      // Always use multipart form data for the new API format
      const formData = new FormData();

      // Add JSON data as string (only if there are post data changes)
      if (postData && Object.keys(postData).length > 0) {
        const postDataJson = JSON.stringify(postData);
        formData.append('post_data', postDataJson);
        console.log('[PostService] post_data JSON being sent:', postDataJson);
        if (postData.scheduled_time) {
          console.log('[PostService] scheduled_time details:', {
            rawValue: postData.scheduled_time,
            parsedDate: new Date(postData.scheduled_time),
            isValidDate: !isNaN(new Date(postData.scheduled_time).getTime())
          });
        }
      }
      
      // Handle media updates if provided (new efficient system)
      if (mediaUpdates && Array.isArray(mediaUpdates)) {
        let fileCount = 0;
        let urlCount = 0;
        
        // Process each media item in order
        mediaUpdates.forEach((mediaItem, index) => {
          if (mediaItem.type === 'file' && mediaItem.file instanceof File) {
            // New file upload - use media[index] format
            formData.append(`media[${index}]`, mediaItem.file);
            fileCount++;
          } else if (mediaItem.type === 'url' && mediaItem.url) {
            // Existing media reference - use media_url[index] format
            formData.append(`media_url[${index}]`, mediaItem.url);
            urlCount++;
          }
        });

        this.logInfo('Adding media updates to FormData (efficient system)', {
          totalItems: mediaUpdates.length,
          newFiles: fileCount,
          existingUrls: urlCount,
          mediaTypes: mediaUpdates.map(m => m.type)
        });

        mediaCounts = { fileCount, urlCount, totalItems: mediaUpdates.length };
      }

      // Use the apiHook's makeRequest with isFormData flag for multipart upload
      const response = await this.apiHook.makeRequest(API_CONFIG.ENDPOINTS.POSTS.UPDATE(postId), {
        method: 'PUT',
        data: formData,
        isFormData: true
      });

      this.logInfo('Post updated successfully', { postId, status: response.status || 'success' });
      if (mediaCounts && mediaCounts.fileCount > 0) {
        trackEvent('media_upload', {
          entity: 'post',
          file_count: mediaCounts.fileCount,
          url_count: mediaCounts.urlCount,
          total_items: mediaCounts.totalItems
        });
      }
      return response;
    } catch (error) {
      this.logError('Failed to update post', error);
      throw error;
    }
  }

  /**
   * Update post status (approve/unapprove)
   * @param {string} postId - Post ID
   * @param {string} status - New status ('scheduled' or 'draft')
   * @returns {Promise<Object>} Status update result
   */
  async updatePostStatus(postId, status) {
    await this.ensureInitialized();
    this.logInfo('Updating post status', { postId, status });

    try {
      const response = await this.apiHook.put(`/api/v1/posts/${postId}/status`, {
        status: status
      });
      this.logInfo('Post status updated successfully', { postId, status, responseStatus: response.status });
      return response;
    } catch (error) {
      this.logError('Failed to update post status', error);
      throw error;
    }
  }

  /**
   * Approve a post
   * @param {string} postId - Post ID
   * @returns {Promise<Object>} Approval result
   */
  async approvePost(postId) {
    await this.ensureInitialized();
    this.logInfo('Approving post', { postId });

    try {
      const response = await this.apiHook.post(API_CONFIG.ENDPOINTS.POSTS.APPROVE(postId), {
        approved: true,
        notes: 'Approved via calendar interface'
      });
      this.logInfo('Post approved successfully', { postId, status: response.status });
      trackEvent('post_approve');
      return response;
    } catch (error) {
      this.logError('Failed to approve post', error);
      throw error;
    }
  }

  /**
   * Schedule a post
   * @param {string} postId - Post ID
   * @param {string} scheduledTime - ISO datetime string
   * @returns {Promise<Object>} Schedule result
   */
  async schedulePost(postId, scheduledTime) {
    await this.ensureInitialized();
    this.logInfo('Scheduling post', { postId, scheduledTime });

    try {
      const response = await this.apiHook.post(API_CONFIG.ENDPOINTS.POSTS.SCHEDULE(postId), {
        scheduled_time: scheduledTime
      });
      this.logInfo('Post scheduled successfully', { postId, scheduledTime, status: response.status });
      return response;
    } catch (error) {
      this.logError('Failed to schedule post', error);
      throw error;
    }
  }

  /**
   * Delete a post
   * @param {string} postId - Post ID
   * @returns {Promise<Object>} Deletion result
   */
  async deletePost(postId) {
    await this.ensureInitialized();
    this.logInfo('Deleting post', { postId });

    try {
      const response = await this.apiHook.delete(API_CONFIG.ENDPOINTS.POSTS.DELETE(postId));
      this.logInfo('Post deleted successfully', { postId, status: response.status });
      return response;
    } catch (error) {
      this.logError('Failed to delete post', error);
      throw error;
    }
  }

  /**
   * Perform bulk operations on multiple posts
   * @param {string} action - Action type (delete, approve, schedule)
   * @param {Array<string>} postIds - Array of post IDs
   * @param {Object} options - Additional options (e.g., scheduled_time for bulk schedule)
   * @returns {Promise<Object>} Bulk operation result
   */
  async bulkOperation(action, postIds, options = {}) {
    await this.ensureInitialized();
    this.logInfo('Performing bulk operation', { action, postIds, options });

    try {
      const requestBody = { post_ids: postIds, ...options };
      const response = await this.apiHook.post(API_CONFIG.ENDPOINTS.POSTS.BULK(action), requestBody);
      
      this.logInfo('Bulk operation completed successfully', { 
        action, 
        affectedPosts: postIds.length,
        status: response.status
      });
      
      return response;
    } catch (error) {
      this.logError('Failed to perform bulk operation', error);
      throw error;
    }
  }

  /**
   * Generate posts for a campaign
   * @param {string} campaignId - Campaign ID
   * @param {Object} options - Generation options
   * @returns {Promise<Object>} Generated posts
   */
  async generatePostsForCampaign(campaignId, options = {}) {
    await this.ensureInitialized();
    this.logInfo('Generating posts for campaign', { campaignId, options });

    try {
      // Use the campaigns endpoint for generation as per current structure
      const endpoint = `/api/v1/campaigns/${campaignId}/generate`;
      const response = await this.apiHook.post(endpoint, options);
      
      this.logInfo('Posts generated successfully', {
        campaignId,
        generatedCount: response.posts?.length || 0,
        status: response.status
      });
      trackEvent('campaign_posts_generate', {
        generated_count: response.posts?.length || 0
      });

      return response;
    } catch (error) {
      this.logError('Failed to generate posts', error);
      throw error;
    }
  }

  /**
   * Regenerate post content only
   * @param {string} postId - Post ID
   * @param {Object} options - Regeneration options
   * @returns {Promise<Object>} Regenerated post
   */
  async regenerateContent(postId, options = {}) {
    await this.ensureInitialized();
    this.logInfo('Regenerating post content', { postId, options });

    try {
      const endpoint = `/api/v1/posts/${postId}/regenerate/content`;
      const response = await this.apiHook.post(endpoint, {
        user_prompt: options.user_prompt || "Improve this post content while maintaining the same message and platform optimization",
        preserve_hashtags: options.preserve_hashtags !== undefined ? options.preserve_hashtags : true,
        preserve_mentions: options.preserve_mentions !== undefined ? options.preserve_mentions : true
      });

      this.logInfo('Post content regenerated successfully', { postId, status: response.status });
      trackEvent('post_regenerate', { regen_type: 'content' });
      return response;
    } catch (error) {
      this.logError('Failed to regenerate post content', error);
      throw error;
    }
  }

  /**
   * Regenerate post image only
   * @param {string} postId - Post ID
   * @param {Object} options - Image regeneration options
   * @returns {Promise<Object>} Regenerated post
   */
  async regenerateImage(postId, options = {}) {
    await this.ensureInitialized();
    this.logInfo('Regenerating post image', { postId, options });

    try {
      const endpoint = `/api/v1/posts/${postId}/regenerate/image`;
      const response = await this.apiHook.post(endpoint, {
        user_prompt: options.user_prompt || "Generate a new image for this post",
        count: options.count || 1,
        ratio: options.ratio || "4:5"
      });

      this.logInfo('Post image regenerated successfully', { postId, status: response.status });
      trackEvent('post_regenerate', { regen_type: 'image' });
      return response;
    } catch (error) {
      this.logError('Failed to regenerate post image', error);
      throw error;
    }
  }

  /**
   * Regenerate complete post (content + image)
   * Creates a new version automatically on the backend
   * @param {string} postId - Post ID
   * @param {Object} options - Complete regeneration options
   * @returns {Promise<Object>} Regenerated post with new version
   */
  async regeneratePost(postId, options = {}) {
    await this.ensureInitialized();
    this.logInfo('Regenerating complete post', { postId, options });

    try {
      const endpoint = `/api/v1/posts/${postId}/regenerate`;
      const response = await this.apiHook.post(endpoint, {
        user_prompt: options.user_prompt || "Regenerate this post with fresh content and images",
        preserve_hashtags: options.preserve_hashtags !== undefined ? options.preserve_hashtags : false,
        preserve_mentions: options.preserve_mentions !== undefined ? options.preserve_mentions : false,
        image_count: options.image_count || 1,
        image_ratio: options.image_ratio || "4:5"
      });

      this.logInfo('Complete post regenerated successfully', {
        postId,
        status: response.status,
        newVersionCreated: true
      });
      trackEvent('post_regenerate', { regen_type: 'full' });
      return response;
    } catch (error) {
      this.logError('Failed to regenerate complete post', error);
      throw error;
    }
  }

}

// Create and export singleton instance
const postService = new PostService();
export default postService;
